<template>
  <div class="face-recognition-container">
    <h2>Reconhecimento Facial Automático</h2>

    <div class="info-bar">
      <p>
        <!--   IP: <strong>{{ userIp ? userIp : "-" }}</strong> -->
      </p>
      <p>
        Localização: <strong>{{ formatLocation(latitude, longitude) }}</strong>
      </p>
      <p v-if="locationError" class="error-message small">
        {{ locationError }}
      </p>
    </div>

    <div class="main-layout-grid">
      <div class="grid-col left-column">
        <div class="camera-feed-wrapper">
          <video
            ref="videoElement"
            autoplay
            playsinline
            class="camera-feed"
          ></video>
          <canvas ref="canvasElement" style="display: none"></canvas>
          <canvas ref="overlayCanvas" class="overlay-canvas"></canvas>
        </div>

        <div class="controls">
          <button
            @click="startCamera"
            :disabled="cameraActive"
            class="btn primary"
          >
            {{ cameraActive ? "Câmera Ativa" : "Abrir Câmera" }}
          </button>
          <button
            @click="stopCamera"
            :disabled="!cameraActive"
            class="btn secondary"
          >
            Parar Câmera
          </button>
        </div>

        <p v-if="!modelsLoaded && cameraActive" class="loading-message">
          Carregando modelos de detecção facial...
        </p>
        <p
          v-if="modelsLoaded && cameraActive && !recognizing"
          class="status-message"
        >
          Aguardando rosto para reconhecimento automático...
        </p>
        <div v-if="errorMessage" class="error-box">
          <p class="error-message">Erro: {{ errorMessage }}</p>
        </div>
      </div>

      <div class="grid-col right-column">
        <h3>Últimas Identificações</h3>
        <div class="table-container">
          <b-table
            v-if="identifications.length > 0"
            striped
            hover
            :items="identifications"
            :fields="tableFields"
            class="identification-table"
            sticky-header="480px"
            responsive="sm"
          >
            <template #cell(recognized)="data">
              <span
                :class="{
                  'text-success': data.item.recognized,
                  'text-danger': !data.item.recognized,
                }"
              >
                {{ data.item.recognized ? "✅ Sim" : "❌ Não" }}
              </span>
            </template>
            <template #cell(timestamp)="data">
              {{ formatTimestamp(data.item.timestamp) }}
            </template>
            <template #cell(ip)="data">
              {{ data.item.ip || "N/A" }}
            </template>
            <template #cell(location)="data">
              {{ formatLocation(data.item.latitude, data.item.longitude) }}
            </template>
          </b-table>
          <p v-else class="no-identifications-message">
            Nenhuma identificação realizada ainda.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import * as faceapi from "face-api.js";
import { mapState } from "vuex";
import { baseApiUrl, showError } from "@/global";

export default {
  name: "FaceRecognition",

  computed: { ...mapState(["user"]) },

  data() {
    return {
      videoElement: null,
      canvasElement: null,
      overlayCanvas: null,
      mediaStream: null,
      cameraActive: false,
      recognizing: false,
      recognitionResult: null,
      errorMessage: "",
      backendUrl: baseApiUrl + "/recognize_face", // "http://localhost:3018/recognize_face", // AJUSTE SE NECESSÁRIO
      clienteId: null,
      modelsLoaded: false,
      detectionInterval: null,
      detectionFrequency: 1000,
      lastRecognitionTime: 0,
      recognitionCooldown: 5000,

      identifications: [],
      tableFields: [
        { key: "timestamp", label: "Hora", sortable: true },
        { key: "nome", label: "Nome", sortable: true },
        { key: "recognized", label: "Reconhecido?", sortable: true },
        {
          key: "mappedSimilarity",
          label: "Similaridade (%)",
          sortable: true,
          formatter: (value) => (value ? value.toFixed(2) : "N/A"),
        },
        { key: "ip", label: "IP", sortable: false }, // Nova coluna IP
        { key: "location", label: "Localização", sortable: false }, // Nova coluna Localização
        { key: "message", label: "Mensagem" },
      ],
      maxTableEntries: 10,

      // Novas propriedades para IP e Localização
      userIp: "Aguardando IP...",
      latitude: "Aguardando Latitude...",
      longitude: "Aguardando Longitude...",
      locationError: "",
    };
  },
  async mounted() {
    this.videoElement = this.$refs.videoElement;
    this.canvasElement = this.$refs.canvasElement;
    this.overlayCanvas = this.$refs.overlayCanvas;

    await this.loadFaceApiModels();
    this.getPublicIp(); // Inicia a busca pelo IP
    this.getCurrentLocation(); // Inicia a busca pela localização
  },
  beforeUnmount() {
    this.stopCamera();
    if (this.detectionInterval) {
      clearInterval(this.detectionInterval);
    }
  },
  methods: {
    async loadFaceApiModels() {
      try {
        this.errorMessage = "Carregando modelos de detecção facial...";
        await faceapi.nets.tinyFaceDetector.loadFromUri("/models");
        await faceapi.nets.faceLandmark68Net.loadFromUri("/models");
        this.modelsLoaded = true;
        this.errorMessage = "";
        console.log("Modelos da face-api.js carregados com sucesso!");
      } catch (error) {
        console.error("Erro ao carregar modelos da face-api.js:", error);
        this.errorMessage =
          "Falha ao carregar modelos de detecção facial. Verifique o console.";
      }
    },

    async startCamera() {
      this.errorMessage = "";
      this.recognitionResult = null;
      if (!this.modelsLoaded) {
        this.errorMessage =
          "Modelos de detecção facial ainda não carregados. Por favor, aguarde.";
        return;
      }

      try {
        this.mediaStream = await navigator.mediaDevices.getUserMedia({
          video: true,
        });
        this.videoElement.srcObject = this.mediaStream;
        this.cameraActive = true;

        this.videoElement.onloadedmetadata = () => {
          this.overlayCanvas.width = this.videoElement.videoWidth;
          this.overlayCanvas.height = this.videoElement.videoHeight;
          this.startDetectionLoop();
        };
      } catch (err) {
        console.error("Erro ao acessar a câmera:", err);
        this.errorMessage =
          "Não foi possível acessar a câmera. Verifique as permissões.";
        this.cameraActive = false;
      }
    },

    stopCamera() {
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach((track) => track.stop());
        this.videoElement.srcObject = null;
        this.cameraActive = false;
      }
      if (this.detectionInterval) {
        clearInterval(this.detectionInterval);
        this.detectionInterval = null;
      }
      this.overlayCanvas
        .getContext("2d")
        .clearRect(0, 0, this.overlayCanvas.width, this.overlayCanvas.height);
    },

    startDetectionLoop() {
      if (this.detectionInterval) {
        clearInterval(this.detectionInterval);
      }
      this.detectionInterval = setInterval(async () => {
        if (!this.cameraActive || !this.modelsLoaded || this.recognizing) {
          return;
        }

        try {
          const detections = await faceapi.detectAllFaces(
            this.videoElement,
            new faceapi.TinyFaceDetectorOptions()
          );

          const displaySize = {
            width: this.videoElement.videoWidth,
            height: this.videoElement.videoHeight,
          };
          faceapi.matchDimensions(this.overlayCanvas, displaySize);
          this.overlayCanvas
            .getContext("2d")
            .clearRect(0, 0, displaySize.width, displaySize.height);

          const resizedDetections = faceapi.resizeResults(
            detections,
            displaySize
          );
          faceapi.draw.drawDetections(this.overlayCanvas, resizedDetections);

          if (detections.length > 0) {
            const now = Date.now();
            if (now - this.lastRecognitionTime > this.recognitionCooldown) {
              console.log("Rosto detectado! Iniciando reconhecimento...");
              this.lastRecognitionTime = now;
              this.recognizeFace();
            }
          }
        } catch (error) {
          console.error("Erro na detecção de rosto:", error);
          this.errorMessage = "Erro na detecção de rosto. Tente novamente.";
          this.recognizing = false;
        }
      }, this.detectionFrequency);
    },

    async recognizeFace() {
      if (!this.cameraActive || this.recognizing) {
        return;
      }

      this.recognizing = true;
      this.errorMessage = "";

      try {
        const videoWidth = this.videoElement.videoWidth;
        const videoHeight = this.videoElement.videoHeight;

        this.canvasElement.width = videoWidth;
        this.canvasElement.height = videoHeight;

        const context = this.canvasElement.getContext("2d");
        context.drawImage(this.videoElement, 0, 0, videoWidth, videoHeight);

        const photoBase64 = this.canvasElement.toDataURL("image/jpeg", 0.8);
        const cleanPhotoBase64 = photoBase64.split(",")[1];

        // Prepara os dados de IP e localização para enviar ao backend
        const ipToSend =
          this.userIp !== "Aguardando IP..." &&
          this.userIp !== "Não foi possível obter o IP."
            ? this.userIp
            : null;
        const latitudeToSend =
          this.latitude !== "Aguardando Latitude..." && this.latitude !== "N/A"
            ? this.latitude
            : null;
        const longitudeToSend =
          this.longitude !== "Aguardando Longitude..." &&
          this.longitude !== "N/A"
            ? this.longitude
            : null;

        const response = await axios.post(this.backendUrl, {
          cliente: this.user.psg,
          photoBase64: cleanPhotoBase64,
          ip: ipToSend,
          latitude: latitudeToSend,
          longitude: longitudeToSend,
        });

        // Adiciona IP e Localização ao objeto de resultado antes de adicionar à tabela
        const resultWithLocation = {
          ...response.data, // Mantém os dados originais do reconhecimento
          ip: ipToSend,
          latitude: latitudeToSend,
          longitude: longitudeToSend,
        };

        this.recognitionResult = resultWithLocation; // Armazena o último resultado com a localização
        this.addIdentificationToTable(resultWithLocation); // Adiciona à tabela

        if (this.recognitionResult.recognized) {
          console.log("Reconhecimento bem-sucedido! Cooldown ativado.");
          this.recognitionCooldown = 15000;
        } else {
          this.recognitionCooldown = 5000;
        }
      } catch (error) {
        console.error("Erro ao reconhecer o rosto:", error);
        this.errorMessage =
          error.response?.data?.message ||
          "Erro desconhecido ao processar o reconhecimento.";

        // Cria um objeto de erro com dados de IP e localização
        const errorResult = {
          recognized: false,
          message: this.errorMessage,
          userId: null,
          nome: null,
          similarity: null,
          mappedSimilarity: null,
          ip:
            this.userIp !== "Aguardando IP..." &&
            this.userIp !== "Não foi possível obter o IP."
              ? this.userIp
              : null,
          latitude:
            this.latitude !== "Aguardando Latitude..." &&
            this.latitude !== "N/A"
              ? this.latitude
              : null,
          longitude:
            this.longitude !== "Aguardando Longitude..." &&
            this.longitude !== "N/A"
              ? this.longitude
              : null,
        };
        this.recognitionResult = errorResult;
        this.addIdentificationToTable(errorResult);
      } finally {
        this.recognizing = false;
      }
    },

    addIdentificationToTable(result) {
      const newEntry = {
        timestamp: new Date(),
        userId: result.userId || "N/A",
        nome: result.nome || "N/A",
        recognized: result.recognized,
        mappedSimilarity: result.mappedSimilarity,
        message: result.message,
        ip: result.ip, // Adiciona o IP ao objeto de entrada da tabela
        latitude: result.latitude, // Adiciona a latitude ao objeto de entrada da tabela
        longitude: result.longitude, // Adiciona a longitude ao objeto de entrada da tabela
      };

      this.identifications.unshift(newEntry);
      if (this.identifications.length > this.maxTableEntries) {
        this.identifications.pop();
      }
    },
    formatTimestamp(date) {
      if (!date) return "N/A";
      return new Date(date).toLocaleTimeString("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      });
    },

    // Exemplo para Node.js com proxy

    async getPublicIp() {
      try {
        const response = await axios.get("https://api.ipify.org?format=json", {
          httpsAgent: proxyAgent, // Para HTTPS
          httpAgent: proxyAgent, // Para HTTP
        });
        this.userIp = response.data.ip;
        console.log("IP Público:", this.userIp);
      } catch (error) {
        console.error("Erro ao obter o IP público:", error);
        console.error("Erro completo:", error); // Imprima o erro completo
        this.userIp = "Não foi possível obter o IP.";
      }
    },

    getCurrentLocation() {
      if (navigator.geolocation) {
        this.locationError = "Tentando obter sua localização...";
        navigator.geolocation.getCurrentPosition(
          (position) => {
            this.latitude = position.coords.latitude;
            this.longitude = position.coords.longitude;
            this.locationError = "";
            console.log(
              "Latitude:",
              this.latitude,
              "Longitude:",
              this.longitude
            );
          },
          (error) => {
            switch (error.code) {
              case error.PERMISSION_DENIED:
                this.locationError =
                  "Permissão de localização negada. Recarregue a página para tentar novamente.";
                break;
              case error.POSITION_UNAVAILABLE:
                this.locationError = "Informação de localização indisponível.";
                break;
              case error.TIMEOUT:
                this.locationError =
                  "Tempo esgotado ao tentar obter a localização.";
                break;
              default:
                this.locationError =
                  "Erro desconhecido ao obter a localização.";
                break;
            }
            console.error("Erro ao obter localização:", error);
            this.latitude = "N/A";
            this.longitude = "N/A";
          },
          {
            enableHighAccuracy: true,
            timeout: 5000,
            maximumAge: 0,
          }
        );
      } else {
        this.locationError =
          "Geolocalização não é suportada por este navegador.";
        this.latitude = "N/A";
        this.longitude = "N/A";
      }
    },
    formatLocation(lat, lng) {
      if (lat === "N/A" || lng === "N/A") return "N/A";
      if (lat === "Aguardando Latitude..." || lng === "Aguardando Longitude...")
        return "Aguardando...";
      return `${lat.toFixed(4)}, ${lng.toFixed(4)}`; // Formata para 4 casas decimais
    },
  },
};
</script>

<style scoped>
/* Adicione estilos para a nova info-bar */
.info-bar {
  display: flex;
  justify-content: space-around;
  margin-bottom: 20px;
  background-color: #eaf6ff;
  padding: 10px 15px;
  border-radius: 8px;
  box-shadow: 0 1px 5px rgba(0, 0, 0, 0.05);
  flex-wrap: wrap; /* Permite quebrar em linhas menores */
}

.info-bar p {
  margin: 5px 10px; /* Espaçamento entre os itens */
  color: #333;
  font-size: 0.95em;
}

.info-bar strong {
  color: #007bff;
}

.error-message.small {
  font-size: 0.8em;
  color: #dc3545;
  margin-top: 5px;
  text-align: center;
  width: 100%; /* Ocupa toda a largura para a mensagem de erro */
}

/* Mantenha o restante dos seus estilos CSS exatamente como estão */
.face-recognition-container {
  font-family: "Arial", sans-serif;
  max-width: 1800px;
  margin: 40px auto;
  padding: 25px;
  border-radius: 10px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  background-color: #f9f9f9;
}

h2 {
  color: #333;
  margin-bottom: 25px;
  font-size: 1.8em;
  text-align: center;
}

.main-layout-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  justify-content: center;
}

.grid-col {
  flex: 1;
  min-width: 300px;
  box-sizing: border-box;
  padding: 10px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.left-column,
.right-column {
  flex-basis: calc(50% - 10px);
  max-width: calc(50% - 10px);
}

@media (max-width: 768px) {
  .left-column,
  .right-column {
    flex-basis: 100%;
    max-width: 100%;
  }
}

.camera-feed-wrapper {
  position: relative;
  width: 100%;
  padding-bottom: 75%;
  background-color: #000;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}

.camera-feed,
.overlay-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transform: scaleX(-1);
}

.overlay-canvas {
  z-index: 10;
}

.controls {
  margin-bottom: 25px;
  display: flex;
  gap: 15px;
  justify-content: center;
}

.btn {
  padding: 12px 25px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 1em;
  transition: background-color 0.3s ease, transform 0.2s ease;
  flex-grow: 1;
  max-width: 200px;
}

.btn:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
  transform: none;
}

.btn.primary {
  background-color: #007bff;
  color: white;
}

.btn.primary:hover:not(:disabled) {
  background-color: #0056b3;
  transform: translateY(-2px);
}

.btn.secondary {
  background-color: #28a745;
  color: white;
}

.btn.secondary:hover:not(:disabled) {
  background-color: #218838;
  transform: translateY(-2px);
}

.result-box,
.error-box {
  padding: 15px;
  border-radius: 8px;
  margin-top: 20px;
  font-size: 1.1em;
  text-align: left;
}

.result-box h3 {
  margin-top: 0;
  color: #555;
  font-size: 1.3em;
  margin-bottom: 10px;
}

.success-message {
  color: #28a745;
  background-color: #e6ffe6;
  border: 1px solid #c3e6cb;
  padding: 10px;
  border-radius: 5px;
}

.error-message {
  color: #dc3545;
  background-color: #ffe6e6;
  border: 1px solid #f5c6cb;
  padding: 10px;
  border-radius: 5px;
}

.loading-message,
.status-message {
  margin-top: 15px;
  padding: 10px;
  background-color: #e0f7fa;
  border: 1px solid #b2ebf2;
  color: #00796b;
  border-radius: 5px;
  font-size: 0.95em;
  text-align: center;
}

.right-column h3 {
  text-align: center;
  margin-bottom: 20px;
  color: #333;
}

.table-container {
  overflow-x: auto;
}

.identification-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 0;
}

.identification-table th,
.identification-table td {
  padding: 12px 15px;
  border-bottom: 1px solid #ddd;
  text-align: left;
}

.identification-table th {
  background-color: #f2f2f2;
  font-weight: bold;
  color: #555;
}

.identification-table tr:hover {
  background-color: #f5f5f5;
}

.text-success {
  color: #28a745 !important;
  font-weight: bold;
}

.text-danger {
  color: #dc3545 !important;
  font-weight: bold;
}

.no-identifications-message {
  text-align: center;
  color: #777;
  padding: 20px;
  border: 1px dashed #ccc;
  border-radius: 5px;
  margin-top: 20px;
  background-color: #fefefe;
}
</style>